import { Controller } from '@hotwired/stimulus';

const TICK_MS = 65;
const TICKS = 17;

// The Random Tutorial reel. Three stacked rows spin through a preloaded list of
// titles and land on one; the links point at whatever is showing.
//
// One instance drives both surfaces — the desktop rail panel and the mobile
// sheet — so the title payload is only inlined once. Targets are plural because
// each surface has its own set of rows.
export default class extends Controller {
  static targets = ['top', 'middle', 'bottom', 'link', 'sheet'];
  static values = { tutorials: Array };

  connect() {
    this.index = Math.floor(Math.random() * Math.max(this.tutorialsValue.length, 1));
    this.spinning = false;
    this.render();
  }

  disconnect() {
    this.stop();
    document.body.classList.remove('overflow-hidden');
  }

  // Mobile: the button is a real link to a random tutorial, so it still works
  // without JS. With JS we intercept it and spin instead.
  openSheet(event) {
    if (event) event.preventDefault();
    if (!this.hasSheetTarget) return;

    this.sheetTarget.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
    this.spin();
  }

  closeSheet() {
    if (!this.hasSheetTarget) return;

    this.sheetTarget.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  }

  closeOnScrim(event) {
    if (event.target === event.currentTarget) this.closeSheet();
  }

  spin() {
    if (this.spinning || this.tutorialsValue.length === 0) return;

    this.spinning = true;
    this.setSpinningStyles(true);

    let ticks = 0;
    this.timer = setInterval(() => {
      ticks += 1;
      this.index = (this.index + 1) % this.tutorialsValue.length;
      this.render();

      if (ticks >= TICKS) {
        this.stop();
        this.spinning = false;
        this.setSpinningStyles(false);
      }
    }, TICK_MS);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  setSpinningStyles(on) {
    this.rowTargets.forEach((row) => { row.style.filter = on ? 'blur(1.2px)' : ''; });
    [...this.topTargets, ...this.bottomTargets].forEach((row) => {
      row.style.opacity = on ? '0.35' : '';
    });
    // Nothing to click until it lands, or you jump to whatever flashed past.
    this.linkTargets.forEach((link) => { link.style.pointerEvents = on ? 'none' : ''; });
  }

  render() {
    const list = this.tutorialsValue;
    if (list.length === 0) return;

    const at = (offset) => list[(this.index + offset + list.length) % list.length];

    this.topTargets.forEach((row) => { row.textContent = at(-1).title; });
    this.bottomTargets.forEach((row) => { row.textContent = at(1).title; });

    const landed = at(0);
    this.middleTargets.forEach((row) => { row.textContent = landed.title; });
    this.linkTargets.forEach((link) => { link.href = landed.path; });
  }

  get rowTargets() {
    return [...this.topTargets, ...this.middleTargets, ...this.bottomTargets];
  }
}
